import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
// Same folder level as DefaultAssigneeSection. If this file sits directly in src/components/, change to "../utils/ErrorModal"
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

const labelStyle = {
  display: "block",
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK_2,
};
const fieldSx = {
  mt: 0.75,
  "& .MuiOutlinedInput-root": {
    height: 42,
    borderRadius: "10px",
    background: "#FFFFFF",
    fontFamily: MONO,
    fontSize: 13,
    color: INK,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
  "& input::placeholder": { fontFamily: FONT, color: "#8A97A8", opacity: 1 },
};

const ProjectTelegram = ({
  projectId,
  telegramBotId,
  telegramChatId,
  isTelegramAllowed,
  onUpdated,
}) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));

  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const showModal = (msg, type) => {
    setModalMsg(msg);
    setModalType(type);
    setModalOpen(true);
  };

  const [formData, setFormData] = useState({
    telegramBotId: telegramBotId || "",
    telegramChatId: telegramChatId || "",
    isTelegramAllowed: isTelegramAllowed || false,
  });

  const [isExisting, setIsExisting] = useState(telegramBotId && telegramChatId);

    useEffect(() => {
    setFormData({
      telegramBotId: telegramBotId || "",
      telegramChatId: telegramChatId || "",
      isTelegramAllowed: isTelegramAllowed || false,
    });
    setIsExisting(!!(telegramBotId && telegramChatId));
  }, [telegramBotId, telegramChatId, isTelegramAllowed]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/project/update-telegram?projectId=${projectId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      showModal(
        isExisting ? "Telegram settings updated" : "Telegram settings added",
        "success",
      );
      setIsExisting(true);
      await onUpdated?.();
    } catch (error) {
      console.log(error);

      showModal(
        error?.response?.data?.Exception || "Failed to save telegram settings",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 0,
        border: `1px solid ${LINE}`,
        borderRadius: "14px",
        background: "#fff",
        overflow: "hidden",
        fontFamily: FONT,
        color: INK,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.125,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #EDF1F5",
        }}
      >
        <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: "#17A2B8", flexShrink: 0 }} />
        <Typography variant="h6" fontWeight={700} mb={0} sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", color: INK }}>
          Telegram Configuration
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 2, pb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <label style={labelStyle}>Telegram Bot Token</label>

            <TextField
              fullWidth
              placeholder="Enter telegram bot id"
              value={formData.telegramBotId}
              onChange={(e) => handleChange("telegramBotId", e.target.value)}
              sx={fieldSx}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <label style={labelStyle}>Telegram Chat ID</label>

            <TextField
              fullWidth
              placeholder="Enter telegram chat id"
              value={formData.telegramChatId}
              onChange={(e) => handleChange("telegramChatId", e.target.value)}
              sx={fieldSx}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0,
                px: 1,
                py: 0.5,
                background: formData.isTelegramAllowed ? "#EAF2FB" : "#F7F9FC",
                border: `1px solid ${formData.isTelegramAllowed ? "#8FC3E6" : LINE}`,
                borderRadius: "10px",
                transition: "background .15s, border-color .15s",
              }}
            >
              <Switch
                checked={formData.isTelegramAllowed}
                onChange={(e) =>
                  handleChange("isTelegramAllowed", e.target.checked)
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#FFFFFF" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { background: PRIMARY, opacity: 1 },
                  "& .MuiSwitch-track": { background: "#C3CDD9", opacity: 1, borderRadius: 999 },
                  "& .MuiSwitch-thumb": { boxShadow: "0 1px 2px rgba(15,27,42,0.3)" },
                }}
              />

              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: formData.isTelegramAllowed ? 700 : 600, color: INK }}>
                Enable Telegram Notifications
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            className="btn-contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              height: 38,
              px: 2.5,
              borderRadius: "9px !important",
              background: `${PRIMARY} !important`,
              color: "#FFFFFF !important",
              border: "0 !important",
              fontFamily: FONT,
              fontSize: 13.5,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: "#084767 !important" },
              "&.Mui-disabled": { opacity: 0.6 },
            }}
          >
            {loading ? "Saving..." : isExisting ? "Update" : "Add"}
          </Button>
        </Box>
      </Box>

      <ErrorModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        msg={modalMsg}
        type={modalType}
      />
    </Box>
  );
};

export default ProjectTelegram;